import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem
} from "@/components/ui/menubar";
import {
  Calendar,
  Dumbbell,
  UtensilsCrossed,
  Bell,
  ClipboardCheck,
  Home as HomeIcon,
  Clock,
  TrendingUp,
  Target,
  Loader2
} from "lucide-react";
import Navigation from "./Navigation";
import { clientApi, Client, workoutPlanApi, WorkoutPlan } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const menuItems = [
  { label: "Home", icon: HomeIcon, to: "/" },
  { label: "Calendar", icon: Calendar, to: "/calendar" },
  { label: "Gym", icon: Dumbbell, to: "/gym-plan" },
  { label: "Diet", icon: UtensilsCrossed, to: "/diet" },
  { label: "Assessments", icon: ClipboardCheck, to: "/assessments" },
];

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, clientName, isLoading, sessionId } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [workoutPlans, setWorkoutPlans] = useState<WorkoutPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Get client name from authentication context
  const getClientName = () => {
    return clientName || "Guest"; // Use the authenticated client name from AuthContext
  };

  // Redirect to auth if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch client data and workout plans
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    const clientName = getClientName();
    console.log(`Home page loading for client: ${clientName}, session: ${sessionId}`);
    if (clientName === "Guest") {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Fetch client data
    clientApi.getAll()
      .then(clients => {
        const foundClient = clients.find(c => 
          c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        
        if (foundClient) {
          setClient(foundClient);
          
          // Fetch workout plans for this client
          return workoutPlanApi.getAll(foundClient._id);
        } else {
          setError('Client not found');
          return Promise.resolve([]);
        }
      })
      .then(plans => {
        setWorkoutPlans(plans);
      })
      .catch(err => {
        console.error('Error fetching client or workout plans:', err);
        setError('Failed to load client data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, clientName]);

  // Calculate workout days count (how many days have exercises out of 7)
  const getWorkoutDaysCount = () => {
    if (!workoutPlans.length) return "0/7";
    
    const activePlan = workoutPlans.find(p => p.status === 'active') || workoutPlans[0];
    if (!activePlan?.workoutSessions) return "0/7";
    
    const daysWithExercises = activePlan.workoutSessions.filter(session => 
      session.exercises && session.exercises.length > 0
    ).length;
    
    return `${daysWithExercises}/7`;
  };

  // Get daily calories from BMR calculation
  const getDailyCalories = () => {
    if (!client?.diet || !client.diet.length) return "0";
    
    // Get the most recent BMR calculation
    const latestDiet = client.diet.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
    
    return latestDiet.calories?.toString() || "0";
  };

  // Calculate streak days (days since client was created)
  const getStreakDays = () => {
    if (!client?.createdAt) return "0";
    
    const createdDate = new Date(client.createdAt);
    const today = new Date();
    
    // Reset time to start of day for accurate day calculation
    createdDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - createdDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays).toString();
  };
  
  const todaySchedule = [
    { time: "08:00 AM", activity: "Morning Workout", type: "gym", icon: Dumbbell },
    { time: "12:30 PM", activity: "Nutrition Check-in", type: "diet", icon: UtensilsCrossed },
    { time: "03:00 PM", activity: "Trainer Meeting", type: "meeting", icon: Calendar },
  ];

  const quickStats = [
    { 
      label: "Workouts This Week", 
      value: loading ? "..." : getWorkoutDaysCount(), 
      icon: Dumbbell, 
      color: "bg-primary" 
    },
    { 
      label: "Daily Calories Goal", 
      value: loading ? "..." : getDailyCalories(), 
      icon: Target, 
      color: "bg-success" 
    },
    { 
      label: "Streak Days", 
      value: loading ? "..." : getStreakDays(), 
      icon: TrendingUp, 
      color: "bg-accent" 
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <div className="relative h-64 md:h-80 bg-gradient-to-r from-primary/20 to-accent/20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/30 to-accent/30"></div>
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="text-center text-white px-4">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Welcome back, {clientName || "Guest"}! 💪
            </h1>
            <p className="text-lg md:text-xl opacity-90">
              Ready to crush your wellness goals today?
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {quickStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="h-fit">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Today's Schedule */}
          <div className="lg:col-span-2">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>Today's Schedule</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {todaySchedule.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div key={index} className="flex items-center space-x-4 p-4 rounded-lg bg-secondary/50 hover:bg-secondary/70 transition-colors">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">{item.activity}</h4>
                          <p className="text-sm text-muted-foreground">{item.time}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {item.type}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Quick Links */}
          <div>
            <Card className="h-fit">
              <CardHeader>
                <CardTitle>Quick Access</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Link to="/calendar">
                    <Button variant="outline" className="w-full justify-start" size="lg">
                      <Calendar className="w-4 h-4 mr-2" />
                      View Calendar
                    </Button>
                  </Link>
                  <Link to="/gym-plan">
                    <Button variant="outline" className="w-full justify-start" size="lg">
                      <Dumbbell className="w-4 h-4 mr-2" />
                      Today's Workout
                    </Button>
                  </Link>
                  <Link to="/diet">
                    <Button variant="outline" className="w-full justify-start" size="lg">
                      <UtensilsCrossed className="w-4 h-4 mr-2" />
                      Meal Plan
                    </Button>
                  </Link>

                  <Link to="/assessments">
                    <Button variant="outline" className="w-full justify-start" size="lg">
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                      Assessments
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;